import React, { useEffect, useRef, useState } from 'react'
import styles from './DesktopWindows.module.scss'
import { AiOutlineClose, AiOutlineMinus } from 'react-icons/ai'
import { BiWindows, BiWindow } from 'react-icons/bi'

import { useAppDispatch, useAppSelector } from '../../../../hooks/reduxHooks'
import { State } from '../../../../redux/store.types'
import {
  windowsBottomLimit,
  windowTopLimit,
  windowLeftLimit,
  windowRightLimit,
  windowsMinHeight,
  windowsMinWidth
} from '../../desktop.constants'
import { CSSTransition } from 'react-transition-group'
import {
  changeWindowLatestAxis,
  changeWindowLatestSize,
  closeWindow,
  maximizeWindow,
  minimizeWindow,
  moveWindowToTop,
  setIsChangingWindow,
  unMaximizeWindow
} from '../../../../redux/slices/WindowSlice/WindowSlice'
import { openedWindow } from '../../../../redux/slices/WindowSlice/WindowSlice.types'
import DesktopWindowRenderer from './DesktopWindowRenderer'
import CustomConfirmAlert from '../../../../components/customConfirmAlert/CustomConfirmAlert'

export interface CloseConfirmAlert {
  title?: string
  message?: string
  aceptBtn?: string
  cancelBtn?: string
}

declare global {
  interface Window {
    getUserToken: any
  }
}
const DesktopWindow: ReactFC<
  openedWindow & { appContainerEl: React.RefObject<HTMLDivElement> }
> = (props) => {
  const iframeRefEl = useRef<HTMLIFrameElement>(null)
  const { windowLatestAxis, windowLatestSize, appContainerEl } = props

  const [alertConfirmOnClose, setAlertConfirmOnClose] = useState<
    CloseConfirmAlert | undefined
  >(undefined)
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false)
  const [windowZIndex, setWindowZIndex] = useState(0)
  const [clientMouseOrigin, setClientMouseOrigin] = useState({
    x: 0,
    y: 0
  })
  const [isDraggin, setIsDraggin] = useState(false)
  const [isResizing, setIsResizing] = useState({
    type: '',
    isResizing: false
  })
  const [dragginStyle, setDragginStyle] = useState(false)

  const [currentWindowAxis, setCurrentWindowAxis] = useState({
    x: windowLatestAxis.x,
    y: windowLatestAxis.y
  })

  const [transformAxis, setTransformAxis] = useState({
    x: windowLatestAxis.x,
    y: windowLatestAxis.y
  })

  const [currentWindowSize, setCurrentWindowSize] = useState({
    height: windowLatestSize.height,
    width: windowLatestSize.width
  })

  const [minimizeExited, setMinimizeExited] = useState(false)
  const windowContainerRef = useRef<HTMLDivElement>(null)

  // redux hooks
  const dispatch = useAppDispatch()
  const { windowsOrder, isChangingWindow } = useAppSelector(
    (state: State) => state.window
  )

  useEffect(() => {
    const ZIndex =
      windowsOrder.findIndex((windowId) => windowId === props.id) * 100
    setWindowZIndex(ZIndex)
  }, [windowsOrder, props.id])

  const cleanIframe = () => {
    console.log(iframeRefEl.current?.parentNode)
    iframeRefEl.current?.parentNode?.removeChild(iframeRefEl.current)
  }

  useEffect(() => {
    return () => {
      cleanIframe()
    }
  }, [])

  const handleClose = () => {
    if (alertConfirmOnClose !== undefined) {
      return setConfirmCloseDialogOpen(true)
    }
    cleanIframe()
    dispatch(
      closeWindow({
        id: props.id
      })
    )
  }

  const handleMinimize = () => {
    dispatch(
      minimizeWindow({
        id: props.id
      })
    )
  }

  const handleMaximize = (event: React.MouseEvent) => {
    event.preventDefault()
    if (props.maximized) {
      return dispatch(
        unMaximizeWindow({
          id: props.id
        })
      )
    }
    dispatch(
      maximizeWindow({
        id: props.id
      })
    )
  }

  const handleDragBegin = (event: React.MouseEvent) => {
    event.preventDefault()
    if (!(event.target === event.currentTarget) || props.maximized) return
    setIsDraggin(true)
    dispatch(
      setIsChangingWindow({
        isChangingWindow: true
      })
    )
    setClientMouseOrigin({
      x: event.clientX,
      y: event.clientY
    })
  }

  // handle drag events
  useEffect(() => {
    const preventXAxisLimitTrespassing = (xToMove: number) => {
      // Prevent trespassing left limit
      if (xToMove <= -currentWindowSize.width + windowLeftLimit) {
        return -currentWindowSize.width + windowLeftLimit
      }
      if (xToMove + windowRightLimit > window.innerWidth) {
        return window.innerWidth - windowRightLimit
      }
      return xToMove
    }

    const preventYAxisLimitTresspassing = (yToMove: number) => {
      if (yToMove < windowTopLimit) {
        return windowTopLimit
      }
      if (yToMove > appContainerEl.current!.offsetHeight - windowsBottomLimit) {
        return appContainerEl.current!.offsetHeight - windowsBottomLimit
      }
      return yToMove
    }

    const handleDrag = (event: MouseEvent) => {
      if (!isDraggin) return
      setDragginStyle(true)
      const xToMove = windowLatestAxis.x + event.clientX - clientMouseOrigin.x
      const yToMove = windowLatestAxis.y + event.clientY - clientMouseOrigin.y
      const newCurrentX = preventXAxisLimitTrespassing(xToMove)
      const newCurrentY = preventYAxisLimitTresspassing(yToMove)
      setTransformAxis({
        x: newCurrentX,
        y: newCurrentY
      })
    }
    const handleDragOver = () => {
      setIsDraggin(false)
      setDragginStyle(false)
      setCurrentWindowAxis(transformAxis)
      dispatch(
        setIsChangingWindow({
          isChangingWindow: false
        })
      )
      dispatch(
        changeWindowLatestAxis({
          id: props.id,
          x: transformAxis.x,
          y: transformAxis.y
        })
      )
    }
    if (isDraggin) {
      window.addEventListener('mousemove', handleDrag)
      window.addEventListener('mouseup', handleDragOver)
    }
    return () => {
      window.removeEventListener('mousemove', handleDrag)
      window.removeEventListener('mouseup', handleDragOver)
    }
  }, [
    clientMouseOrigin,
    isDraggin,
    windowLatestAxis,
    currentWindowAxis,
    dispatch,
    props.id,
    appContainerEl,
    currentWindowSize,
    transformAxis
  ])

  const handleResizeBegin = (event: React.MouseEvent) => {
    event.preventDefault()
    if (props.maximized) return
    const resizeType = event.currentTarget.getAttribute('resize-type')
    setIsResizing({
      type: resizeType ?? '',
      isResizing: true
    })
    setClientMouseOrigin({
      x: event.clientX,
      y: event.clientY
    })
    dispatch(
      setIsChangingWindow({
        isChangingWindow: true
      })
    )
  }
  useEffect(() => {
    interface newWindowStateI {
      height: number
      width: number
      x: number
      y: number
    }
    const preventTopLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      // if windows goes above browser
      if (validatedNewState.y <= windowTopLimit) {
        validatedNewState = {
          ...validatedNewState,
          height: currentWindowSize.height,
          y: currentWindowAxis.y
        }
      }
      return validatedNewState
    }
    const preventBottomLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      if (
        validatedNewState.height + validatedNewState.y >
        appContainerEl.current!.offsetHeight
      ) {
        validatedNewState = {
          ...validatedNewState,
          height: currentWindowSize.height
        }
      }
      return validatedNewState
    }
    const preventLeftLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      if (validatedNewState.x < 0) {
        validatedNewState = {
          ...validatedNewState,
          x: currentWindowAxis.x,
          width: currentWindowSize.width
        }
      }
      return validatedNewState
    }

    const preventRightLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      if (validatedNewState.x + validatedNewState.width > window.innerWidth) {
        validatedNewState = {
          ...validatedNewState,
          x: currentWindowAxis.x,
          width: currentWindowSize.width
        }
      }
      return validatedNewState
    }

    const preventTopLeftLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      validatedNewState = preventLeftLimitTrespassing(
        preventTopLimitTrespassing(validatedNewState)
      )
      return validatedNewState
    }
    const preventTopRightLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      validatedNewState = preventTopLimitTrespassing(
        preventRightLimitTrespassing(validatedNewState)
      )
      return validatedNewState
    }
    const preventBottomLeftLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      validatedNewState = preventBottomLimitTrespassing(
        preventLeftLimitTrespassing(validatedNewState)
      )
      return validatedNewState
    }
    const preventBottomRightLimitTrespassing = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      validatedNewState = preventBottomLimitTrespassing(
        preventRightLimitTrespassing(validatedNewState)
      )
      return validatedNewState
    }
    const preventResizeMinHeight = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      if (validatedNewState.height < windowsMinHeight) {
        validatedNewState = {
          ...validatedNewState,
          height: currentWindowSize.height,
          y: currentWindowAxis.y
        }
      }
      return validatedNewState
    }
    const preventResizeMinWidth = (
      newWindowState: newWindowStateI
    ): newWindowStateI => {
      let validatedNewState = newWindowState
      if (validatedNewState.width < windowsMinWidth) {
        validatedNewState = {
          ...validatedNewState,
          width: currentWindowSize.width,
          x: currentWindowAxis.x
        }
      }
      return validatedNewState
    }
    const handleResize = (event: MouseEvent) => {
      let newWindowState = {
        height: currentWindowSize.height,
        width: currentWindowSize.width,
        x: currentWindowAxis.x,
        y: currentWindowAxis.y
      }
      let heightToResize
      let widthToResize
      switch (isResizing.type) {
        case 'top':
          heightToResize = clientMouseOrigin.y - event.clientY
          newWindowState = {
            ...newWindowState,
            height: windowLatestSize.height + heightToResize,
            y: windowLatestAxis.y - heightToResize
          }
          newWindowState = preventTopLimitTrespassing(
            preventResizeMinHeight(newWindowState)
          )
          break
        case 'bottom':
          heightToResize = event.clientY - clientMouseOrigin.y
          newWindowState = {
            ...newWindowState,
            height: windowLatestSize.height + heightToResize
          }
          newWindowState = preventBottomLimitTrespassing(
            preventResizeMinHeight(newWindowState)
          )
          break
        case 'left':
          widthToResize = clientMouseOrigin.x - event.clientX
          newWindowState = {
            ...newWindowState,
            width: windowLatestSize.width + widthToResize,
            x: windowLatestAxis.x - widthToResize
          }
          newWindowState = preventLeftLimitTrespassing(
            preventResizeMinWidth(newWindowState)
          )
          break
        case 'right':
          widthToResize = event.clientX - clientMouseOrigin.x
          newWindowState = {
            ...newWindowState,
            width: windowLatestSize.width + widthToResize
          }
          newWindowState = preventRightLimitTrespassing(
            preventResizeMinWidth(newWindowState)
          )
          break
        case 'top_left':
          heightToResize = clientMouseOrigin.y - event.clientY
          widthToResize = clientMouseOrigin.x - event.clientX
          newWindowState = {
            width: windowLatestSize.width + widthToResize,
            x: windowLatestAxis.x - widthToResize,
            height: windowLatestSize.height + heightToResize,
            y: windowLatestAxis.y - heightToResize
          }
          newWindowState = preventTopLeftLimitTrespassing(
            preventResizeMinWidth(preventResizeMinHeight(newWindowState))
          )
          break
        case 'top_right':
          heightToResize = clientMouseOrigin.y - event.clientY
          widthToResize = event.clientX - clientMouseOrigin.x
          newWindowState = {
            ...newWindowState,
            width: windowLatestSize.width + widthToResize,
            height: windowLatestSize.height + heightToResize,
            y: windowLatestAxis.y - heightToResize
          }
          newWindowState = preventTopRightLimitTrespassing(
            preventResizeMinHeight(preventResizeMinWidth(newWindowState))
          )
          break
        case 'bottom_right':
          heightToResize = event.clientY - clientMouseOrigin.y
          widthToResize = event.clientX - clientMouseOrigin.x
          newWindowState = {
            ...newWindowState,
            width: windowLatestSize.width + widthToResize,
            height: windowLatestSize.height + heightToResize
          }
          newWindowState = preventBottomRightLimitTrespassing(
            preventResizeMinHeight(preventResizeMinWidth(newWindowState))
          )
          break
        case 'bottom_left':
          heightToResize = event.clientY - clientMouseOrigin.y
          widthToResize = clientMouseOrigin.x - event.clientX
          newWindowState = {
            ...newWindowState,
            width: windowLatestSize.width + widthToResize,
            x: windowLatestAxis.x - widthToResize,
            height: windowLatestSize.height + heightToResize
          }
          newWindowState = preventBottomLeftLimitTrespassing(
            preventResizeMinWidth(preventResizeMinHeight(newWindowState))
          )
          break
        default:
          break
      }
      setCurrentWindowSize({
        height: newWindowState.height,
        width: newWindowState.width
      })
      setCurrentWindowAxis({
        y: newWindowState.y,
        x: newWindowState.x
      })
      setTransformAxis({
        y: newWindowState.y,
        x: newWindowState.x
      })
    }
    const handleResizeOver = () => {
      setIsResizing({
        ...isResizing,
        isResizing: false
      })
      dispatch(
        changeWindowLatestAxis({
          id: props.id,
          ...currentWindowAxis
        })
      )
      dispatch(
        changeWindowLatestSize({
          id: props.id,
          ...currentWindowSize
        })
      )
      dispatch(
        setIsChangingWindow({
          isChangingWindow: false
        })
      )
    }
    if (isResizing.isResizing) {
      window.addEventListener('mousemove', handleResize)
      window.addEventListener('mouseup', handleResizeOver)
    }
    return () => {
      window.removeEventListener('mousemove', handleResize)
      window.removeEventListener('mouseup', handleResizeOver)
    }
  }, [
    appContainerEl,
    isResizing,
    clientMouseOrigin,
    windowLatestAxis,
    windowLatestSize,
    currentWindowAxis,
    currentWindowSize,
    dispatch,
    props.id
  ])

  // reset window axis on resize browser window
  useEffect(() => {
    const onClientBrowserResize = () => {
      if (currentWindowAxis.x > window.innerWidth) {
        const newX = window.innerWidth - currentWindowSize.width
        setCurrentWindowAxis((updatedWindowAxis) => ({
          ...updatedWindowAxis,
          x: newX
        }))
        dispatch(
          changeWindowLatestAxis({
            id: props.id,
            y: props.windowLatestAxis.y,
            x: newX
          })
        )
      }
      if (currentWindowAxis.y > appContainerEl.current!.offsetHeight) {
        const newY = 0
        setCurrentWindowAxis((updatedWindowAxis) => ({
          ...updatedWindowAxis,
          y: newY
        }))
        dispatch(
          changeWindowLatestAxis({
            id: props.id,
            x: props.windowLatestAxis.x,
            y: newY
          })
        )
      }
    }
    let afterResizeTimeOut: NodeJS.Timeout
    window.onresize = () => {
      clearTimeout(afterResizeTimeOut)
      afterResizeTimeOut = setTimeout(() => {
        onClientBrowserResize()
      }, 500)
    }
    return () => {
      window.onresize = null
    }
  }, [
    currentWindowSize,
    currentWindowAxis,
    props.id,
    props.windowLatestAxis,
    dispatch,
    appContainerEl
  ])

  const handleDoubleClickWindow = (event: React.MouseEvent) => {
    event.preventDefault()
    if (props.maximized) {
      return dispatch(
        unMaximizeWindow({
          id: props.id
        })
      )
    }
    dispatch(
      maximizeWindow({
        id: props.id
      })
    )
  }

  // reset minimized state
  useEffect(() => {
    if (!props.minimized) setMinimizeExited(false)
  }, [props.minimized])

  return (
    <CSSTransition
      in={!props.minimized}
      timeout={250}
      classNames={{
        exitActive: styles.desktopWindowExitActive,
        exitDone: styles.desktopWindowExitDone
      }}
      onExited={() => {
        setMinimizeExited(true)
      }}
    >
      <div
        style={{
          top: `${props.maximized ? '0' : currentWindowAxis.y}px`,
          left: `${props.maximized ? '0' : currentWindowAxis.x}px`,
          width: `${props.maximized ? '100%' : `${currentWindowSize.width}px`}`,
          height: `${
            props.maximized ? '100%' : `${currentWindowSize.height}px`
          }`,
          zIndex: windowZIndex,
          display: props.minimized && minimizeExited ? 'none' : 'flex',
          transform: isDraggin
            ? `translate(${transformAxis.x - windowLatestAxis.x}px, ${
                transformAxis.y - windowLatestAxis.y
              }px)`
            : ''
        }}
        className={`${styles.desktopWindow} ${
          dragginStyle ? styles.dragging : ''
        } ${isChangingWindow ? styles.changing : ''}`}
        onMouseDown={() => {
          if (windowsOrder[windowsOrder.length - 1] !== props.id) {
            dispatch(
              moveWindowToTop({
                id: props.id
              })
            )
          }
        }}
      >
        <div
          className={styles.topBar}
          onMouseDown={handleDragBegin}
          onDoubleClick={handleDoubleClickWindow}
        >
          <div
            className={styles.topBarLeftSide}
            onMouseDown={(event) => {
              event.preventDefault()
              setIsDraggin(true)
              dispatch(
                setIsChangingWindow({
                  isChangingWindow: true
                })
              )
              setClientMouseOrigin({
                x: event.clientX,
                y: event.clientY
              })
            }}
          >
            <img src={props.icon} alt='' />
            <span className={styles.topBarLeftSide_title}>{props.name}</span>
          </div>
          <div className={styles.topBarRightSide}>
            <div onClick={handleMinimize} className={styles.topBar_Btns}>
              <AiOutlineMinus />
            </div>
            <div onClick={handleMaximize} className={styles.topBar_Btns}>
              {props.maximized ? <BiWindows /> : <BiWindow />}
            </div>
            <div onClick={handleClose} className={styles.topBar_Btns}>
              <AiOutlineClose />
            </div>
          </div>
        </div>
        <div className={`${styles.body}`}>
          <div className={styles.bodyContainer} ref={windowContainerRef}>
            <DesktopWindowRenderer
              app={props.app!}
              windowId={props.id}
              setAlertConfirmOnClose={setAlertConfirmOnClose}
              windowContainerRef={windowContainerRef}
              iframe={
                props.iFrame
                  ? {
                      title: props.name,
                      url: props.iFrame
                    }
                  : undefined
              }
              iframeRef={iframeRefEl}
            />
            <CustomConfirmAlert
              isOpen={confirmCloseDialogOpen}
              onClose={() => {
                setConfirmCloseDialogOpen(false)
              }}
              onConfirm={() => {
                setAlertConfirmOnClose(undefined)
                setConfirmCloseDialogOpen(false)
                dispatch(
                  closeWindow({
                    id: props.id
                  })
                )
              }}
              title={alertConfirmOnClose?.title!}
              message={alertConfirmOnClose?.message!}
              cancelBtn={alertConfirmOnClose?.cancelBtn!}
              acceptBtn={alertConfirmOnClose?.aceptBtn!}
            />
          </div>
        </div>
        <div
          className={`${styles.resizeTop} ${styles.resize}`}
          resize-type='top'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeBottom} ${styles.resize}`}
          resize-type='bottom'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeLeft} ${styles.resize}`}
          resize-type='left'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeRight} ${styles.resize}`}
          resize-type='right'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeCornerTopLeft} ${styles.resize}`}
          resize-type='top_left'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeCornerTopRight} ${styles.resize}`}
          resize-type='top_right'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeCornerBottomLeft} ${styles.resize}`}
          resize-type='bottom_left'
          onMouseDown={handleResizeBegin}
        />
        <div
          className={`${styles.resizeCornerBottomRight} ${styles.resize}`}
          resize-type='bottom_right'
          onMouseDown={handleResizeBegin}
        />
      </div>
    </CSSTransition>
  )
}

export default DesktopWindow
